import { ensureTonConnectButtonRoot, getTonConnectManifestUrl, TON_CONNECT_CONNECTION_TIMEOUT } from './config';
import i18n from '../../i18n';

class TonConnectService {
  constructor() {
    this.ui = null;
    this.initPromise = null;
    this.statusSubscribers = new Set();
    this.unsubscribeStatus = null;
  }

  notifyStatus(wallet) {
    const payload = {
      wallet: wallet || null,
      connected: Boolean(wallet),
      address: this.getWalletAddress(wallet),
    };

    this.statusSubscribers.forEach((callback) => {
      try {
        callback(payload);
      } catch (error) {
        console.error('[TonConnectService] Status subscriber failed:', error);
      }
    });
  }

  subscribeStatus(callback) {
    if (typeof callback !== 'function') {
      return () => {};
    }

    this.statusSubscribers.add(callback);
    if (this.ui) {
      callback({
        wallet: this.ui.wallet || null,
        connected: this.ui.connected,
        address: this.getWalletAddress(),
      });
    }

    return () => {
      this.statusSubscribers.delete(callback);
    };
  }

  async init() {
    if (this.ui) {
      return this.ui;
    }

    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = (async () => {
      ensureTonConnectButtonRoot();
      const manifestUrl = getTonConnectManifestUrl();
      const { TonConnectUI } = await import('@tonconnect/ui');

      this.ui = new TonConnectUI({
        manifestUrl,
        buttonRootId: 'ton-connect-button-root',
        restoreConnection: true,
      });

      if (!this.unsubscribeStatus) {
        this.unsubscribeStatus = this.ui.onStatusChange((wallet) => {
          this.notifyStatus(wallet);
        });
      }

      // Уведомляем текущий статус
      this.notifyStatus(this.ui.wallet || null);

      this.initPromise = null;
      return this.ui;
    })();

    return this.initPromise;
  }

  getWallet() {
    return this.ui?.wallet || null;
  }

  getWalletAddress(wallet) {
    const targetWallet = wallet || this.getWallet();
    if (!targetWallet) {
      return '';
    }

    return (
      targetWallet?.account?.address ||
      targetWallet?.address ||
      targetWallet?.account?.walletAddress ||
      ''
    );
  }

  isConnected() {
    return Boolean(this.ui?.connected);
  }

  async ensureConnected({ timeout = TON_CONNECT_CONNECTION_TIMEOUT } = {}) {
    const ui = await this.init();

    if (ui.connected && ui.wallet) {
      return ui.wallet;
    }

    await ui.openModal();

    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        off();
        reject(new Error(i18n.t('wallet.connect_timeout', 'Connection timeout. Please try again.')));
      }, timeout);

      const off = ui.onStatusChange((wallet) => {
        if (wallet) {
          clearTimeout(timer);
          off();
          resolve(wallet);
        }
      });
    });
  }

  async connect(options = {}) {
    return this.ensureConnected(options);
  }

  async disconnect() {
    try {
      const ui = await this.init();
      await ui.disconnect();
      this.notifyStatus(null);
    } catch (error) {
      console.warn('[TonConnectService] Disconnect failed:', error);
    }
  }

  async sendTransaction(transaction, options = {}) {
    if (!transaction) {
      throw new Error('Transaction payload is required.');
    }

    const ui = await this.init();
    const wallet = await this.ensureConnected(options);
    const result = await ui.sendTransaction(transaction);

    return {
      result,
      wallet: ui.wallet || wallet || null,
    };
  }
}

const tonConnectService = new TonConnectService();

export default tonConnectService;

